import React, { useState, useEffect } from 'react';
import { Copy } from 'lucide-react';
import Modal2, { Modal2Footer, Modal2Button } from '../../../common/Modal2';
import Input from '../../../common/Input';
import { TemplateItem } from '../types';

interface DuplicateTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (newTitle: string) => void;
  loading?: boolean;
  template: TemplateItem | null;
}

/** Duplicate-template dialog — Modal2 shell, same validation contract. */
const DuplicateTemplateModal: React.FC<DuplicateTemplateModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  loading = false,
  template,
}) => {
  const [newTitle, setNewTitle] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen && template) {
      setNewTitle(`${template.title} (Copy)`);
      setError('');
    }
  }, [isOpen, template]);

  const handleConfirm = () => {
    const trimmedTitle = newTitle.trim();
    if (!trimmedTitle) { setError('Title is required'); return; }
    if (trimmedTitle.length < 3) { setError('Title must be at least 3 characters'); return; }
    if (trimmedTitle.length > 100) { setError('Title must be less than 100 characters'); return; }
    setError('');
    onConfirm(trimmedTitle);
  };

  return (
    <Modal2
      isOpen={isOpen}
      onClose={onClose}
      title="Duplicate Template"
      subtitle={
        template
          ? <>Create a copy of <span className="font-bold">“{template.title}”</span>. The new template will contain all existing questions.</>
          : undefined
      }
      width={480}
      footer={
        <Modal2Footer>
          <Modal2Button variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Modal2Button>
          <Modal2Button
            variant="primary"
            onClick={handleConfirm}
            loading={loading}
            disabled={loading}
          >
            <Copy className="w-4 h-4" />
            Create Copy
          </Modal2Button>
        </Modal2Footer>
      }
    >
      <Input
        label="New Template Title"
        value={newTitle}
        onChange={(v) => {
          setNewTitle(v as string);
          setError('');
        }}
        placeholder="Enter new title"
        required
        error={error}
      />
    </Modal2>
  );
};

export default DuplicateTemplateModal;
